import questionsData from '../data/360-questions.json';

// Translations interface for comment templates
interface CommentTemplates {
  positive: string[];
  improvement: string[];
  neutral: string[];
}

interface TranslationData {
  commentTemplates: CommentTemplates;
  categoryNames: {
    management: string;
    organization: string;
    repeatSales: string;
    finance: string;
    operations: string;
    qualityControl: string;
    newSales: string;
    stress: string;
    principles: string;
    helpAcceptance: string;
  };
}

// Types
interface Response {
  questionId: string;
  answer: string;
  questionText?: string;
}

interface Question {
  id: string;
  questionNumber: number;
  text: { he: string; en: string };
  reverseScored: boolean;
  category: string;
}

interface CategoryAnalysis {
  categoryCode: string;
  categoryName: { he: string; en: string };
  positiveComments: string[];
  constructiveComments: string[];
  neutralComments: string[];
  score: number;
  totalQuestions: number;
  positiveCount: number;
  neutralCount: number;
  needsImprovementCount: number;
}

export class EpokAnalysisService {
  private questions: Question[];
  private categories: { [key: string]: { he: string; en: string } } = {
    'A': { he: 'ניהול', en: 'Executive' },
    'B': { he: 'ארגון', en: 'Organization' },
    'C': { he: 'מכירות חוזרות', en: 'Repeat Sales' },
    'D': { he: 'פיננסים', en: 'Finance' },
    'E': { he: 'תפעול', en: 'Operations' },
    'F': { he: 'בקרת איכות', en: 'Quality Assurance' },
    'G': { he: 'מכירות חדשות', en: 'New Sales' },
    'H': { he: 'מתח', en: 'Stress' },
    'I': { he: 'עקרונות', en: 'Principles' },
    'J': { he: 'עזרה', en: 'Help' }
  };

  private reverseQuestionIds = new Set([
    'qu3', 'qu8', 'qu9', 'qu11', 'qu12', 'qu14', 'qu18', 'qu19', 'qu21', 'qu24',
    'qu25', 'qu26', 'qu28', 'qu29', 'qu31', 'qu32', 'qu33', 'qu35', 'qu36', 'qu38',
    'qu39', 'qu41', 'qu42', 'qu45', 'qu48', 'qu49', 'qu54', 'qu55', 'qu57', 'qu58',
    'qu59', 'qu63', 'qu67', 'qu68', 'qu69', 'qu71', 'qu72', 'qu74', 'qu75', 'qu76',
    'qu78', 'qu80', 'qu81', 'qu90', 'qu93', 'qu98'
  ]);

  constructor() {
    // Extract all questions from the JSON data
    this.questions = [];
    questionsData.assessment.pages.forEach(page => {
      page.questions.forEach(q => {
        this.questions.push({
          id: q.id,
          questionNumber: q.questionNumber,
          text: q.text,
          reverseScored: q.reverseScored,
          category: q.category
        });
      });
    });
  }

  private getScoreForAnswer(questionId: string, answer: string): number {
    const isReverse = this.reverseQuestionIds.has(questionId);
    
    switch (answer) {
      case '+PLUS':
        return isReverse ? 0 : 9;
      case 'Maybe':
        return 3;
      case '-MINUS':
        return isReverse ? 9 : 0;
      default:
        return 3;
    }
  }

  private generateCommentForQuestion(
    question: Question, 
    score: number, 
    language: 'he' | 'en',
    translations: TranslationData,
    answer?: string
  ): string {
    const categoryName = this.categories[question.category][language];
    const questionText = question.text[language];
    
    let templates: string[];
    
    if (score === 9) {
      templates = translations.commentTemplates.positive;
    } else if (score === 0) {
      templates = translations.commentTemplates.improvement;
    } else {
      templates = translations.commentTemplates.neutral;
    }
    
    const template = templates[Math.floor(Math.random() * templates.length)];
    
    // Replace placeholders with actual content
    let comment = template
      .replace('{category}', categoryName)
      .replace('{question}', questionText)
      .replace('{questionText}', questionText);
    
    // Add actual answer in parentheses if provided
    if (answer) {
      const answerText = answer === '+PLUS' ? (language === 'he' ? 'כן' : 'Yes') :
                        answer === '-MINUS' ? (language === 'he' ? 'לא' : 'No') :
                        (language === 'he' ? 'אולי' : 'Maybe');
      comment += ` (${answerText})`;
    }
    
    return comment;
  }

  public analyzeResponses(
    responses: Response[], 
    language: 'he' | 'en' = 'he', 
    translations: TranslationData
  ): CategoryAnalysis[] {
    const categoryAnalysis: { [key: string]: CategoryAnalysis } = {};
    
    // Initialize categories
    Object.keys(this.categories).forEach(categoryCode => {
      categoryAnalysis[categoryCode] = {
        categoryCode,
        categoryName: this.categories[categoryCode],
        positiveComments: [],
        constructiveComments: [],
        neutralComments: [],
        score: 0,
        totalQuestions: 0,
        positiveCount: 0,
        neutralCount: 0,
        needsImprovementCount: 0
      };
    });

    // Process each response
    responses.forEach(response => {
      const question = this.questions.find(q => q.id === response.questionId);
      if (!question) return;

      const score = this.getScoreForAnswer(response.questionId, response.answer);
      const category = categoryAnalysis[question.category];
      
      category.score += score;
      category.totalQuestions++;
      
      // Create question-answer format instead of template comment
      const answerText = response.answer === '+PLUS' ? (language === 'he' ? 'כן' : 'Yes') :
                        response.answer === '-MINUS' ? (language === 'he' ? 'לא' : 'No') :
                        (language === 'he' ? 'אולי' : 'Maybe');
      
      let formattedComment: string;
      if (score === 9) {        
        formattedComment = `"${question.text[language]}" (${answerText})`;
        category.positiveComments.push(formattedComment);
        category.positiveCount++;
      } else if (score === 0) {        
        formattedComment = `"${question.text[language]}" (${answerText})`;
        category.constructiveComments.push(formattedComment);
        category.needsImprovementCount++;
      } else {        
        formattedComment = `"${question.text[language]}" (${answerText})`;
        category.neutralComments.push(formattedComment);
        category.neutralCount++;
      }
    });

    // Calculate final scores as percentages
    Object.values(categoryAnalysis).forEach(category => {
      if (category.totalQuestions > 0) {
        category.score = Math.round((category.score / (category.totalQuestions * 9)) * 100);
      }
    });

    return Object.values(categoryAnalysis);
  }

  public generateDetailedFeedback(
    responses: Response[], 
    language: 'he' | 'en' = 'he',
    translations: TranslationData
  ): string[] {
    const analysis = this.analyzeResponses(responses, language, translations);
    const allComments: string[] = [];

    analysis.forEach(category => {
      // Add category header
      const categoryHeader = language === 'he' 
        ? `=== ${category.categoryName.he} (${category.score}%) ===`
        : `=== ${category.categoryName.en} (${category.score}%) ===`;
      
      allComments.push(categoryHeader);

      // Get all responses for this category with question details
      const categoryResponses = responses.filter(response => {
        const question = this.questions.find(q => q.id === response.questionId);
        return question && question.category === category.categoryCode;
      });

      // Add positive responses first (9s)
      const positiveResponses = categoryResponses.filter(response => {
        const score = this.getScoreForAnswer(response.questionId, response.answer);
        return score === 9;
      });

      if (positiveResponses.length > 0) {
        const strengthsHeader = language === 'he' ? '💪 נקודות חוזק:' : '💪 Strengths:';
        allComments.push(strengthsHeader);
        positiveResponses.forEach(response => {
          const question = this.questions.find(q => q.id === response.questionId);
          if (question) {
            const answerText = response.answer === '+PLUS' ? (language === 'he' ? 'כן' : 'Yes') :
                              response.answer === '-MINUS' ? (language === 'he' ? 'לא' : 'No') :
                              (language === 'he' ? 'אולי' : 'Maybe');
            const categoryName = this.categories[question.category][language];
            const strengthText = language === 'he' ? 'מראה חוזק ב' : 'demonstrates strength in';
            allComments.push(`• "${question.text[language]}" (${answerText}) ${strengthText} ${categoryName}`);
          }
        });
        allComments.push('');
      }

      // Add improvement areas (0s)
      const improvementResponses = categoryResponses.filter(response => {
        const score = this.getScoreForAnswer(response.questionId, response.answer);
        return score === 0;
      });

      if (improvementResponses.length > 0) {
        const improvementHeader = language === 'he' ? '🎯 תחומים לשיפור:' : '🎯 Areas for Improvement:';
        allComments.push(improvementHeader);
        improvementResponses.forEach(response => {
          const question = this.questions.find(q => q.id === response.questionId);
          if (question) {
            const answerText = response.answer === '+PLUS' ? (language === 'he' ? 'כן' : 'Yes') :
                              response.answer === '-MINUS' ? (language === 'he' ? 'לא' : 'No') :
                              (language === 'he' ? 'אולי' : 'Maybe');
            const categoryName = this.categories[question.category][language];
            const improvementText = language === 'he' ? 'דורש שיפור בתחום' : 'requires improvement in';
            allComments.push(`• "${question.text[language]}" (${answerText}) ${improvementText} ${categoryName}`);
          }
        });
        allComments.push('');
      }

      // Add development opportunities (3s)
      const neutralResponses = categoryResponses.filter(response => {
        const score = this.getScoreForAnswer(response.questionId, response.answer);
        return score === 3;
      });

      if (neutralResponses.length > 0) {
        const developmentHeader = language === 'he' ? '🌱 הזדמנויות לפיתוח:' : '🌱 Development Opportunities:';
        allComments.push(developmentHeader);
        neutralResponses.forEach(response => {
          const question = this.questions.find(q => q.id === response.questionId);
          if (question) {
            const answerText = response.answer === '+PLUS' ? (language === 'he' ? 'כן' : 'Yes') :
                              response.answer === '-MINUS' ? (language === 'he' ? 'לא' : 'No') :
                              (language === 'he' ? 'אולי' : 'Maybe');
            const categoryName = this.categories[question.category][language];
            const developmentText = language === 'he' ? 'הזדמנות לפיתוח ב' : 'development opportunity in';
            allComments.push(`• "${question.text[language]}" (${answerText}) ${developmentText} ${categoryName}`);
          }
        });
        allComments.push('');
      }

      allComments.push('---');
    });

    return allComments;
  }
}